import {graphql, useStaticQuery} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import Button from '@/components/button'
import LocalizedLink from '@/components/localized-link'
import {Text} from '@/components/text'
import {Section, Layout} from '@/styles/grid'
import {focusStyles} from '@/styles/utils'
import {tokens, colors} from '@/theme'
import {getJobsByDepartment} from '@/utils/get-jobs-by-department'
import mq from '@/utils/media-query'

import type {GreenhouseJobsQuery} from '@/graphql-types'
import type {GreenhouseListingType} from '@/utils/get-jobs-by-department'

const JOBS_QTY = 3

interface Props {
    headline: string
    text: string
    ctaText: string
    dataTestId?: string
}

export const JobsGrid = ({headline, text, ctaText, dataTestId}: Props) => {
    const {allGreenhouseJob} = useStaticQuery<GreenhouseJobsQuery>(query)
    const sortedJobsByDepartment = getLatestJobsAcrossDepartments(allGreenhouseJob.nodes)

    return (
        <JobsGrigSection as={Section} $variant="two-equal" data-testid={dataTestId}>
            <CopyWrapper>
                <Text $variant="heading-large">{headline}</Text>
                <Text $variant="paragraph-large" $color="muted">
                    {text}
                </Text>
            </CopyWrapper>
            <ListingsWrapper data-testid="jobs-grid-links">
                {sortedJobsByDepartment.map(({gh_Id, title, offices}) => (
                    <ListingLink to={`/careers/jobs/${gh_Id}`} key={gh_Id}>
                        <Text $variant="paragraph-base">{title}</Text>
                        <Text $variant="paragraph-base" $color="lightMuted">
                            {offices.map((office) => office.name).join(', ')}
                        </Text>
                    </ListingLink>
                ))}
                {ctaText && (
                    <StyledButton
                        forwardedAs={LocalizedLink}
                        to="/careers/jobs"
                        $variant="outline"
                        data-testid="jobs-grid-cta"
                    >
                        {ctaText}
                    </StyledButton>
                )}
            </ListingsWrapper>
        </JobsGrigSection>
    )
}

const JobsGrigSection = styled(Layout)`
    grid-gap: ${tokens.spacing40};
`

const CopyWrapper = styled.div`
    display: grid;
    grid-gap: ${tokens.spacing12};
    align-content: start;
    max-width: 540px;

    ${mq.min('sm')} {
        grid-gap: ${tokens.spacing20};
    }
`

const ListingsWrapper = styled.div`
    display: grid;
    grid-auto-flow: row;
    grid-gap: ${tokens.spacing20};
`

const ListingLink = styled(LocalizedLink)`
    display: grid;
    padding: ${tokens.spacing20};
    background: ${colors.background.muted};
    border: ${({theme}) => theme.borders.transparent};
    border-radius: ${tokens.arc8};
    grid-gap: ${tokens.spacing10};

    &:hover,
    &:active {
        border-color: ${colors.border.secondary};
    }

    &:focus {
        ${focusStyles()}
    }
`

const StyledButton = styled(Button)`
    ${mq.min('sm')} {
        margin-top: ${tokens.spacing20};
        justify-self: start;
    }
`

const query = graphql`
    query GreenhouseJobs {
        allGreenhouseJob {
            nodes {
                id
                gh_Id
                title
                offices {
                    name
                }
                metadata {
                    id
                    value
                }
                departments {
                    name
                }
            }
        }
    }
`

/**
 * Groups the jobs by department and sorts them by ID (which is autoincremental)
 * and returns JOBS_QTY latest jobs from across all departments
 * NOTE: In the API, theres a field `updated_at`, but at the time we created this, it was returning the same time stamp for all jobs.
 * So we used `gh_Id` instead
 */
const getLatestJobsAcrossDepartments = (listings: GreenhouseListingType) => {
    const jobsByDepartment = getJobsByDepartment(listings)
    const jobs = []

    const jobsByDepartmentSorted = Object.values(jobsByDepartment)
        .map((department) => {
            return department.sort((jobA, jobZ) => {
                return jobZ.gh_Id - jobA.gh_Id
            })
        })
        .sort((departmentA, departmentB) => {
            return departmentB[0].gh_Id - departmentA[0].gh_Id
        })

    for (let i = 0; i < jobsByDepartmentSorted.length; i++) {
        for (let j = 0; j < jobsByDepartmentSorted.length && jobs.length < JOBS_QTY; j++) {
            const currentJob = jobsByDepartmentSorted[j][i]
            /**
             * We don't want to include empty items in out jobs array
             * and we don't want to feature `Open Application` in our Careers page, so we skip it.
             */
            if (currentJob !== undefined && !currentJob.title.startsWith('Open Application')) {
                jobs.push(currentJob)
            }
        }
    }

    return jobs
}
